<template>
  <div 
    class="back-to-top-container"
    :class="{
      'back-to-top-container--visible': isVisible,
      'back-to-top-container--hiding': shouldHide
    }"
  >
    <button class="back-to-top-btn" @click="scrollToTop" aria-label="回到顶部">
      <!-- 大屏幕：显示文字和箭头 -->
      <span class="back-to-top-btn__text">顶部</span>
      <div class="back-to-top-btn__arrow">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M5 15l7-7 7 7"/>
        </svg>
      </div>
      
      <!-- 小屏幕：只显示箭头 -->
      <div class="back-to-top-btn__icon-mobile">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M5 15l7-7 7 7"/>
        </svg>
      </div>
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { scrollTo } from 'vue-scrollto';

// 控制按钮是否可见
const isVisible = ref(false);
// 是否应该隐藏按钮
const shouldHide = ref(false);
// 上一次滚动位置
let lastScrollTop = 0;
// 滚动阈值（显示按钮）
const scrollThreshold = 300;
// 隐藏阈值（向上滚动超过此距离后隐藏）
const hideThreshold = 100;
// 是否允许滚动监听（用于节流）
let allowScrollCheck = true;

// 滚动事件处理
const handleScroll = () => {
  if (!allowScrollCheck) return;
  
  allowScrollCheck = false;
  
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const scrollDirection = scrollTop > lastScrollTop ? 'down' : 'up';
  const scrollDistance = Math.abs(scrollTop - lastScrollTop);
  
  // 向下滚动：超过阈值显示按钮
  if (scrollDirection === 'down' && scrollTop > scrollThreshold) {
    if (!isVisible.value) {
      isVisible.value = true;
      shouldHide.value = false;
    }
  } 
  // 向上滚动：超过隐藏阈值开始隐藏
  else if (scrollDirection === 'up' && isVisible.value) {
    // 向上滚动超过隐藏阈值
    if (scrollDistance > hideThreshold && scrollTop > scrollThreshold) {
      shouldHide.value = true;
      // 隐藏动画结束后完全隐藏
      setTimeout(() => {
        isVisible.value = false;
        shouldHide.value = false;
      }, 300);
    }
    
    // 滚动到接近顶部也隐藏
    if (scrollTop <= scrollThreshold) {
      shouldHide.value = true;
      setTimeout(() => {
        isVisible.value = false;
        shouldHide.value = false;
      }, 300);
    }
  }
  
  lastScrollTop = scrollTop;
  
  // 节流：100ms后允许下一次检查
  setTimeout(() => {
    allowScrollCheck = true;
  }, 100);
};

// 平滑滚动到顶部
const scrollToTop = () => {
  // 点击后立即开始隐藏动画
  shouldHide.value = true;
  
  // 使用 vue-scrollto 进行平滑滚动
  scrollTo({
    duration: 800,
    top: 0,
    easing: 'easeInOut'
  });
  
  // 300ms后完全隐藏按钮（保持原来的时间）
  setTimeout(() => {
    isVisible.value = false;
    shouldHide.value = false;
  }, 300);
};

// 组件挂载
onMounted(() => {
  lastScrollTop = window.scrollY || document.documentElement.scrollTop;
  window.addEventListener('scroll', handleScroll, { passive: true });
});

// 组件卸载
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
/* 基础样式 */
.back-to-top-container {
  position: fixed;
  right: 20px;
  bottom: 30px;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

/* 可见状态 - 弹跳效果 */
.back-to-top-container--visible {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
}

/* 隐藏中状态 */
.back-to-top-container--hiding {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

/* 弹跳出现动画 - 先向上超出去，再回弹 */
@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: translateY(50px) scale(0.8);
  }
  50% {
    opacity: 1;
    transform: translateY(-25px) scale(1.05); /* 向上超出正常位置 */
  }
  70% {
    transform: translateY(5px) scale(0.95); /* 向下回弹一点 */
  }
  85% {
    transform: translateY(-5px) scale(1.02); /* 再向上小幅度弹跳 */
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1); /* 回落到正常位置 */
  }
}

/* 按钮基础样式 */
.back-to-top-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 36px;
  border-radius: 20px;
  background-color: #F4333C;
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(244, 51, 60, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  font-size: 14px;
  padding: 0 16px;
  gap: 6px;
  position: relative;
  overflow: hidden;
}

/* 悬停时的颜色渐变效果 */
.back-to-top-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.7s ease;
}

.back-to-top-btn:hover::before {
  left: 100%;
}

/* 按钮悬停效果 */
.back-to-top-btn:hover {
  background-color: #ff525c;
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(244, 51, 60, 0.35);
}

.back-to-top-btn:active {
  transform: scale(0.98);
}

/* 小屏幕图标默认隐藏 */
.back-to-top-btn__icon-mobile {
  display: none;
}

/* ========== 响应式设计 ========== */

/* 平板尺寸 (768px以下) */
@media (max-width: 768px) {
  .back-to-top-container {
    right: 16px;
    bottom: 20px;
  }
  
  .back-to-top-btn {
    width: 65px;
    height: 32px;
    font-size: 13px;
    padding: 0 12px;
    border-radius: 16px;
  }
}

/* 小屏幕尺寸 (480px以下) - 移动到左侧，变成圆形 */
@media (max-width: 480px) {
  .back-to-top-container {
    left: 16px; /* 移动到左侧 */
    right: auto; /* 取消右侧定位 */
    bottom: 20px;
  }
  
  .back-to-top-btn {
    width: 44px; /* 圆形按钮 */
    height: 44px; /* 与宽度相等 */
    border-radius: 50%; /* 变成圆形 */
    padding: 0;
  }
  
  .back-to-top-btn__text {
    display: none; /* 隐藏文字 */
  }
  
  .back-to-top-btn__arrow {
    display: none; /* 隐藏大屏幕箭头 */
  }
  
  .back-to-top-btn__icon-mobile {
    display: flex; /* 显示移动端图标 */
    align-items: center;
    justify-content: center;
  }
}

/* 极小手机尺寸 (375px以下) */
@media (max-width: 375px) {
  .back-to-top-container {
    left: 12px;
    bottom: 16px;
  }
}

/* 深色模式 */
@media (prefers-color-scheme: dark) {
  .back-to-top-btn {
    background-color: #ff525c;
    box-shadow: 0 4px 12px rgba(255, 82, 92, 0.3);
  }
  
  .back-to-top-btn:hover {
    background-color: #ff6b73;
  }
}

/* 减少动画 */
@media (prefers-reduced-motion: reduce) {
  .back-to-top-container,
  .back-to-top-btn,
  .back-to-top-btn::before {
    transition: none !important;
    animation: none !important;
  }
  
  .back-to-top-container--visible {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
}
</style>